import {
  SupplyIntelBrandRowSchema,
  SupplyIntelWarehouseSchema,
} from "@redotech/redo-inventory-common/supply-intel/brand-list";
import { SupplyIntelTopProductSchema } from "@redotech/redo-inventory-common/supply-intel/brand-products";
import { SupplyIntelBrandUnitRowSchema } from "@redotech/redo-inventory-common/supply-intel/brand-units";
import { SupplyIntelBrandUnitSortKey } from "@redotech/redo-inventory-common/supply-intel/brand-view";
import { AdvancedFilterDataSchema } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import { createTableSortSchema } from "@redotech/redo-views-common/table";
import { unaryRpc } from "@redotech/rpc/definition";
import * as z from "zod";

/** One brand's returned units on Redo's shelves, by SKU, with next steps, from a cached Snowflake snapshot. */
export const getSupplyIntelBrandUnits = unaryRpc({
  name: "getSupplyIntelBrandUnits" as const,
  input: z.object({
    accountId: z.string().regex(/^\d{1,19}$/),
    search: z.string().optional(),
    advancedFiltersData: z.array(AdvancedFilterDataSchema).default([]),
    sort: createTableSortSchema(z.enum(SupplyIntelBrandUnitSortKey)).optional(),
    page: z.number().int().min(0).default(0),
    pageSize: z.number().int().min(1).max(500).default(50),
    /** Start a rebuild of the shared snapshot in the background; honored at most once every 10 minutes. */
    refresh: z.boolean().default(false),
  }),
  output: z.object({
    /** Null until the brand's rows are available. */
    page: z
      .object({
        rows: z.array(SupplyIntelBrandUnitRowSchema),
        totalCount: z.number(),
        /** The brand's list row, rolled up over every row that matches the search and filters. */
        summary: SupplyIntelBrandRowSchema,
        /** The products with the most store value leaving the brand's store, over the matching rows. */
        // Defaulted so an older server's response still parses during a deploy.
        topProducts: z.array(SupplyIntelTopProductSchema).default([]),
        /** Every warehouse the brand has units in, for the warehouse filter. */
        warehouses: z.array(SupplyIntelWarehouseSchema),
        /** Every category path the brand has units in, for the category filter. */
        // Defaulted so an older server's response still parses during a deploy.
        categories: z.array(z.string()).default([]),
        dataAsOf: z.date(),
        configVersion: z.string(),
        /** The snapshot hit its row limit, so some units are missing. */
        truncated: z.boolean(),
      })
      .nullable(),
    /** When the build in progress started, if one is running. */
    buildStartedAt: z.date().nullable(),
  }),
});
